/**
 * Hue Sync Coordinator Service
 * Handles manual leader selection for bridge syncing
 * 
 * Simplified Architecture:
 * - In online mode: Each tab registers a session in the database
 *   and user manually selects which tab/device should sync to the bridge
 * - In standalone mode: Always acts as leader (only one client)
 * - Only one leader per user+bridge (enforced by DB constraint in online mode)
 * - Sessions send heartbeats to stay alive (30s timeout)
 */

import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth'
import { hueBridgeService } from './HueBridgeService'
import { hueLogger } from '@/lib/logger'
import { isStandaloneMode, getDataProvider, TABLES } from '@/lib/dataProvider'

// Generate unique session ID
function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
}

export interface HueSyncSession {
  id: string
  user_id: string
  session_id: string
  bridge_id: string
  bridge_reachable: boolean
  is_leader: boolean
  device_name: string | null
  last_heartbeat: string
  online_since: string
  set_as_leader_at: string | null
  created_at: string
}

export class HueSyncCoordinator {
  private sessionId: string
  private bridgeId: string | null = null
  private userId: string | null = null
  private isRunning = false
  private isStandaloneRunning = false  // Tracks if running in standalone mode
  private heartbeatInterval: NodeJS.Timeout | null = null
  private isBridgeReachable = false
  private deviceName: string | null = null
  private sessionSubscription: any = null
  
  // Cached leader status - updated via realtime subscription
  private cachedIsLeader = false

  private static readonly HEARTBEAT_INTERVAL = 10000 // 10 seconds

  constructor() {
    this.sessionId = generateSessionId()
    hueLogger.info(`🎯 HueSyncCoordinator created with session ID: ${this.sessionId}`)
  }

  /**
   * Check if the coordinator is started and ready
   */
  isStarted(): boolean {
    return this.isRunning && !!this.bridgeId && !!this.userId && !!this.deviceName
  }

  /**
   * Start the coordinator - registers session in database
   * In standalone mode, always acts as leader without DB registration
   */
  async start(bridgeId: string, deviceName: string): Promise<void> {
    if (this.isRunning) {
      hueLogger.warn('HueSyncCoordinator already running')
      return
    }

    const authStore = useAuthStore()
    if (!authStore.user) {
      hueLogger.error('No authenticated user for sync coordinator')
      return
    }

    this.bridgeId = bridgeId
    this.userId = authStore.user.id
    this.deviceName = deviceName
    this.isRunning = true

    hueLogger.info(`🚀 Starting HueSyncCoordinator for bridge: ${bridgeId} (${deviceName})`)

    // Check initial bridge reachability
    this.isBridgeReachable = await this.checkBridgeReachability()
    
    if (!this.isBridgeReachable) {
      hueLogger.warn('⚠️ Bridge not reachable - will keep trying in background')
    }

    // In standalone mode, always be leader - skip DB registration
    if (isStandaloneMode()) {
      this.isStandaloneRunning = true
      hueLogger.info('📱 Standalone mode - acting as leader automatically')
      this.cachedIsLeader = true
      // Start a simple reachability check interval instead of heartbeat
      this.startStandaloneHeartbeat()
      hueLogger.success('✅ HueSyncCoordinator started in standalone mode (always leader)')
      return
    }

    // Online mode - register session in database
    await this.registerSession()

    // Subscribe to leader status changes
    await this.setupLeaderSubscription()

    // Start heartbeat to keep session alive
    this.startHeartbeat()

    hueLogger.success('✅ HueSyncCoordinator started and session registered')
  }

  /**
   * Stop the coordinator - removes session from database
   */
  async stop(): Promise<void> {
    if (!this.isRunning) return

    hueLogger.info('🛑 Stopping HueSyncCoordinator')
    this.isRunning = false

    // Stop heartbeat
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }

    // In standalone mode, nothing to clean up from database
    if (this.isStandaloneRunning) {
      this.isStandaloneRunning = false
      hueLogger.success('✅ HueSyncCoordinator stopped (standalone mode)')
      return
    }

    // Online mode - cleanup realtime subscription
    await this.cleanupLeaderSubscription()

    // Unregister session from database
    await this.unregisterSession()

    hueLogger.success('✅ HueSyncCoordinator stopped')
  }

  /**
   * Check if this session is currently the leader
   * In standalone mode, always returns true
   */
  async checkIsLeader(): Promise<boolean> {
    // In standalone mode, always leader
    if (isStandaloneMode()) {
      return true
    }
    
    // Use info level occasionally for debugging
    if (Math.random() < 0.1) { // 10% of the time
      hueLogger.info(`👑 [Leader Check] Cached leader status: ${this.cachedIsLeader}`)
    }
    return this.cachedIsLeader
  }

  /**
   * Set this session as the leader (called from UI)
   * In standalone mode, this is a no-op since we're always leader
   */
  async setAsLeader(): Promise<boolean> {
    // In standalone mode, already leader
    if (isStandaloneMode()) {
      hueLogger.info('📱 Standalone mode - already acting as leader')
      return true
    }
    
    if (!this.userId || !this.bridgeId || !this.deviceName) {
      hueLogger.warn('Cannot set as leader - coordinator not started (no bridge configured?)')
      return false
    }

    try {
      hueLogger.info(`👑 Setting ${this.deviceName} as leader for bridge ${this.bridgeId}`)

      // Call DB function to atomically set this as leader and unset others
      const { error } = await supabase.rpc('set_hue_sync_leader', {
        p_session_id: this.sessionId,
        p_user_id: this.userId,
        p_bridge_id: this.bridgeId,
        p_device_name: this.deviceName
      })

      if (error) {
        hueLogger.error('Failed to set as leader:', error)
        return false
      }

      // Update cached status immediately (subscription will also update it)
      this.cachedIsLeader = true

      hueLogger.success(`✅ ${this.deviceName} is now the leader`)
      return true
    } catch (error) {
      hueLogger.error('Error setting as leader:', error)
      return false
    }
  }

  /**
   * Get the current leader for this bridge
   * In standalone mode, returns a mock session representing this client
   */
  async getCurrentLeader(): Promise<HueSyncSession | null> {
    // In standalone mode, we are always the leader
    if (isStandaloneMode()) {
      return {
        id: 'standalone',
        user_id: this.userId || '',
        session_id: this.sessionId,
        bridge_id: this.bridgeId || '',
        bridge_reachable: this.isBridgeReachable,
        is_leader: true,
        device_name: this.deviceName,
        last_heartbeat: new Date().toISOString(),
        online_since: new Date().toISOString(),
        set_as_leader_at: new Date().toISOString(),
        created_at: new Date().toISOString()
      }
    }
    
    if (!this.userId || !this.bridgeId) return null

    try {
      const { data, error } = await supabase
        .from('hue_sync_sessions')
        .select('*')
        .eq('bridge_id', this.bridgeId)
        .eq('is_leader', true)
        .not('session_id', 'is', null)  // Only online sessions
        .maybeSingle()

      if (error) {
        hueLogger.error('Failed to get current leader:', error)
        return null
      }

      return data || null
    } catch (error) {
      hueLogger.error('Error getting current leader:', error)
      return null
    }
  }

  /**
   * Get all active sessions for this bridge
   * In standalone mode, returns just this session
   */
  async getAllSessions(): Promise<HueSyncSession[]> {
    // In standalone mode, only one session
    if (isStandaloneMode()) {
      const leader = await this.getCurrentLeader()
      return leader ? [leader] : []
    }
    
    if (!this.userId || !this.bridgeId) return []

    try {
      const { data, error } = await supabase
        .from('hue_sync_sessions')
        .select('*')
        .eq('bridge_id', this.bridgeId)
        .order('online_since', { ascending: true })

      if (error) {
        hueLogger.error('Failed to get sessions:', error)
        return []
      }

      return data || []
    } catch (error) {
      hueLogger.error('Error getting sessions:', error)
      return []
    }
  }

  /**
   * Get session ID
   */
  getSessionId(): string {
    return this.sessionId
  }

  /**
   * Register this session in the database
   * Reactivates existing leader session from this device, or creates new session
   */
  private async registerSession(): Promise<void> {
    if (!this.userId || !this.bridgeId) return

    try {
      // First, check if this device was previously a leader (offline session exists)
      const { data: offlineLeader } = await supabase
        .from('hue_sync_sessions')
        .select('*')
        .eq('bridge_id', this.bridgeId)
        .eq('device_name', this.deviceName)
        .eq('is_leader', true)
        .is('session_id', null)  // Offline sessions have null session_id
        .maybeSingle()

      if (offlineLeader) {
        // Reactivate this device's previous leader session
        const { error } = await supabase
          .from('hue_sync_sessions')
          .update({
            session_id: this.sessionId,
            bridge_reachable: this.isBridgeReachable,
            last_heartbeat: new Date().toISOString(),
            online_since: new Date().toISOString()
          })
          .eq('id', offlineLeader.id)

        if (error) {
          hueLogger.error('Failed to reactivate leader session:', error)
          return
        }

        hueLogger.success(`🔄 Reactivated LEADER session: ${this.sessionId} (${this.deviceName})`)
        return
      }

      // Check if there's already an online leader for this bridge
      const existingLeader = await this.getCurrentLeader()
      const shouldBeLeader = !existingLeader

      // Register new session
      const { error } = await supabase
        .from('hue_sync_sessions')
        .insert({
          user_id: this.userId,
          session_id: this.sessionId,
          bridge_id: this.bridgeId,
          bridge_reachable: this.isBridgeReachable,
          is_leader: shouldBeLeader,
          device_name: this.deviceName,
          last_heartbeat: new Date().toISOString(),
          online_since: new Date().toISOString(),
          set_as_leader_at: shouldBeLeader ? new Date().toISOString() : null
        })

      if (error) {
        hueLogger.error('Failed to register session:', error)
        return
      }

      // Set initial cached status
      this.cachedIsLeader = shouldBeLeader

      if (shouldBeLeader) {
        hueLogger.success(`📝 Session registered as LEADER: ${this.sessionId} (${this.deviceName})`)
      } else {
        hueLogger.info(`📝 Session registered as follower: ${this.sessionId} (${this.deviceName})`)
      }
    } catch (error) {
      hueLogger.error('Error registering session:', error)
    }
  }

  /**
   * Set up realtime subscription for leader status changes
   */
  private async setupLeaderSubscription(): Promise<void> {
    if (this.sessionSubscription) {
      await this.cleanupLeaderSubscription()
    }

    hueLogger.info(`Setting up leader subscription for session: ${this.sessionId}`)

    // Query current state first to ensure cache is accurate
    const { data } = await supabase
      .from('hue_sync_sessions')
      .select('is_leader')
      .eq('session_id', this.sessionId)
      .maybeSingle()

    if (data) {
      this.cachedIsLeader = data.is_leader
      hueLogger.info(`Initial leader status loaded: ${this.cachedIsLeader}`)
    }

    // Then subscribe to updates
    this.sessionSubscription = supabase
      .channel(`sync-session:${this.sessionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'hue_sync_sessions',
          filter: `session_id=eq.${this.sessionId}`
        },
        (payload) => {
          hueLogger.info('Leader status update received:', payload)
          if (payload.new && 'is_leader' in payload.new && typeof payload.new.is_leader === 'boolean') {
            this.cachedIsLeader = payload.new.is_leader
            hueLogger.info(`Updated cachedIsLeader: ${this.cachedIsLeader}`)
          }
        }
      )
      .subscribe()
  }

  /**
   * Cleanup realtime subscription
   */
  private async cleanupLeaderSubscription(): Promise<void> {
    if (this.sessionSubscription) {
      await supabase.removeChannel(this.sessionSubscription)
      this.sessionSubscription = null
      hueLogger.info('🔌 Leader subscription cleaned up')
    }
  }

  /**
   * Unregister this session from the database
   */
  private async unregisterSession(): Promise<void> {
    if (!this.userId) return

    try {
      await supabase.rpc('unregister_hue_sync_session', {
        p_session_id: this.sessionId,
        p_user_id: this.userId
      })

      hueLogger.info(`📝 Session unregistered: ${this.sessionId}`)
    } catch (error) {
      hueLogger.error('Error unregistering session:', error)
    }
  }

  /**
   * Check if the bridge is reachable from this session
   */
  private async checkBridgeReachability(): Promise<boolean> {
    if (!this.bridgeId) return false

    try {
      // Get bridge info from database using dataProvider
      const provider = getDataProvider()
      const bridges = await provider.query<{ ip_address: string }>('hue_bridges', {
        filters: [
          { column: 'bridge_id', operator: 'eq', value: this.bridgeId },
          { column: 'is_active', operator: 'eq', value: true }
        ],
        select: 'ip_address'
      })

      if (!bridges || bridges.length === 0) {
        hueLogger.error('Bridge not found in database')
        return false
      }

      // Try to reach the bridge
      const info = await hueBridgeService.getBridgeInfo(this.bridgeId, bridges[0].ip_address)
      
      const isReachable = !!info
      hueLogger.debug(`${isReachable ? '✅' : '❌'} Bridge ${this.bridgeId} is ${isReachable ? 'reachable' : 'unreachable'}`)
      
      return isReachable
    } catch (error) {
      hueLogger.error('Failed to check bridge reachability:', error)
      return false
    }
  }

  /**
   * Start heartbeat to keep session alive
   */
  private startHeartbeat(): void {
    // Send initial heartbeat immediately
    this.sendHeartbeat()

    // Then send every 10 seconds
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat()
    }, HueSyncCoordinator.HEARTBEAT_INTERVAL)

    hueLogger.info('💓 Heartbeat started (every 10 seconds)')
  }

  /**
   * Start standalone heartbeat - just checks bridge reachability, no DB updates
   */
  private startStandaloneHeartbeat(): void {
    // Check initial reachability
    this.sendHeartbeat()

    // Then check every 10 seconds
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat()
    }, HueSyncCoordinator.HEARTBEAT_INTERVAL)

    hueLogger.info('💓 Standalone heartbeat started (every 10 seconds)')
  }

  /**
   * Send heartbeat - update bridge reachability and last_heartbeat
   */
  private async sendHeartbeat(): Promise<void> {
    if (!this.isRunning || !this.userId || !this.bridgeId) return

    try {
      // Check bridge reachability
      const bridgeReachable = await this.checkBridgeReachability()
      
      // Update local state
      this.isBridgeReachable = bridgeReachable

      // In standalone mode, don't update DB - just track locally
      if (this.isStandaloneRunning) {
        hueLogger.debug(`💓 Standalone heartbeat - bridge ${bridgeReachable ? 'reachable' : 'unreachable'}`)
        return
      }

      // Update session in database (cloud mode only)
      const { error } = await supabase
        .from('hue_sync_sessions')
        .update({
          bridge_reachable: bridgeReachable,
          last_heartbeat: new Date().toISOString()
        })
        .eq('session_id', this.sessionId)

      if (error) {
        hueLogger.error('Failed to update heartbeat:', error)
      }
    } catch (error) {
      hueLogger.error('Error sending heartbeat:', error)
    }
  }
}

// Singleton instance
export const hueSyncCoordinator = new HueSyncCoordinator()


